import dayjs from "dayjs";
import * as Yup from "yup";
import {
  Address,
  EmergencyContact,
  PatientMaritalStatusEnum,
  ReferringAgency,
} from "../../../../services";
import { SUBSCRIBER_SHAPE } from "@/components/app/provider/patient/add-new-patient/insurance/insurance-validation";
import { replaceUnderScoreAddSpace } from "../../../../utils/utils";
import {
  PatientAssessmentsReminderMediumEnum,
  PatientDefaultCommunicationChannelEnum,
  PatientInsurance,
} from "./../../../../services/api";

const NAME_REGEX = /^[A-Za-z]+(?:[\s'-][A-Za-z]+)*$/;

export const MIN_INSURANCE_EFFECTIVE_DATE = dayjs("1900-01-01").startOf("day");
export const MAX_INSURANCE_EFFECTIVE_DATE = dayjs()
  .add(100, "year")
  .endOf("day");

const isEmptyDate = (value: unknown) =>
  value === null || value === undefined || value === "";

const toValidDayjs = (value: unknown) => {
  const date = dayjs(value as any);
  return date.isValid() ? date : null;
};

// Effective Start Date is now restricted at selection time via the date
// picker's minDate (set to Patient DOB). The Yup-based validator below is kept
// commented for reference in case save-time validation is needed again.
// export const validateInsuranceEffectiveStartDate = (
//   value: unknown,
//   dob: unknown
// ): string | true => {
//   if (isEmptyDate(value)) return true;
//   const date = toValidDayjs(value);
//   if (!date) return "Please enter a valid date.";
//   if (date.isBefore(MIN_INSURANCE_EFFECTIVE_DATE)) {
//     return `Effective Start Date cannot be earlier than ${MIN_INSURANCE_EFFECTIVE_DATE.format(
//       "MM/DD/YYYY"
//     )}.`;
//   }
//   if (date.isAfter(MAX_INSURANCE_EFFECTIVE_DATE)) {
//     return "Effective Start Date is unreasonably far in the future.";
//   }
//   const dobDate = toValidDayjs(dob);
//   if (dobDate && date.isBefore(dobDate.startOf("day"))) {
//     return "Insurance Effective Start Date cannot be earlier than Patient Date of Birth.";
//   }
//   return true;
// };

type PatientDetailType = {
  firstName: string;
  middleName: string;
  lastName: string;
  timezone: string;
  birthDate: string | null;
  gender: string;
  maritalStatus: PatientMaritalStatusEnum;
  ssn: string;
  mrn: string;
  languages: any;
  ethnicity?: string;
  race?: string;
  avatar: string;
};

type ProviderInformationType = {
  providerId?: { [key: string]: string };
  locationId?: { [key: string]: string };
  registrationDate?: string;
  defaultCommunicationChannel?: PatientDefaultCommunicationChannelEnum;
  referringAgencies?: Pick<ReferringAgency, "uuid">[];
};

type ContactInformationType = {
  mobileNumber?: string;
  phoneNotAvailable?: boolean;
  faxNumber?: string;
  homePhone?: string;
  email?: string;
  emailNotAvailable?: boolean;
  address?: Address;
  isScriptSure?: boolean;
};

type EmergencyContactType = {
  emergencyContacts?: EmergencyContact[];
};

type PrivacyConsentType = {
  emailConsent?: boolean;
  messageConsent?: boolean;
  callConsent?: boolean;
};

type OtherConsent = {
  patientConsentEntities?: any | undefined;
};

type AssessmentConsentType = {
  assessments?: {
    assessment: { value: string; label: string }[];
    recordedDate?: Date;
    reminderMedium?: PatientAssessmentsReminderMediumEnum;
  };
};

type InsuranceType = {
  patientInsurances?: Omit<
    PatientInsurance,
    "insurancePayer" | "orderOfBenefits"
  > &
    [insurancePayer: any];
  active?: boolean;
  provider?: { [key: string]: string };
};
interface AddressPreferences {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  countryCode: string | null;
}

interface PreferencesTypeParams {
  value?: any;
  label?: any;
  uuid: string;
  name: string;
  preferenceType: string;
  contactNumber: string;
  faxNumber: string;
  address: AddressPreferences;
}

type PreferencesType = {
  /**
   * A single uuid -> name map, matching the generated model and what every
   * submit path builds with `pharmacyLabRadiologyMap.reduce(..., {})`.
   */
  pharmacyLabRadiology: { [key: string]: string } | null;
  pharmacyLabRadiologyMap?: PreferencesTypeParams[] | null;
};

export type NewPatientForm = ProviderInformationType &
  PatientDetailType &
  ContactInformationType &
  EmergencyContactType &
  InsuranceType &
  PreferencesType &
  PrivacyConsentType &
  OtherConsent &
  AssessmentConsentType;

export const ADDRESS_INITIAL_VALUES = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  country: "",
  zipcode: "",
};

export const INITIAL_EM_CONTACT: EmergencyContact[] = [
  {
    firstName: "",
    lastName: "",
    email: undefined,
    mobile: "",
    relationshipWithPatient: undefined,
  },
];

export const INITIAL_PATIENT_INSURANCE: PatientInsurance & {
  provider?: { [key: string]: string };
} = {
  active: true,
  planName: "",
  planType: undefined,
  coverageType: undefined,
  orderOfBenefits: undefined,
  insuranceId: "",
  provider: undefined,
  copayType: "FIXED",
  coInsurance: "",
  authorizations: undefined,
  coverageLevel: undefined,
  claimNumber: "",
  note: "",
  deductibleAmount: "",

  employerName: "",
  employerAddress: ADDRESS_INITIAL_VALUES,
  subscriberFirstName: "",
  subscriberLastName: "",
  subscriberMiddleName: "",
  subscriberSsn: "",
  subscriberGender: undefined,
  subscriberBirthDate: undefined,
  subscriberMobileNumber: "",
  subscriberAddress: ADDRESS_INITIAL_VALUES,

  groupId: "",
  memberId: "",
  groupName: "",
  frontPhoto: "",
  backPhoto: "",
  insuredRelationshipWithPatient: undefined, // Enum value or undefined
  insuredFirstName: "",
  insuredLastName: "",
  address: ADDRESS_INITIAL_VALUES,
  planId: undefined,
  insuredGender: undefined, // Enum value or undefined
  insuredBirthDate: "",
  coPay: "",
  networkStatus: undefined,
  copayAmount: undefined,
  coInsuranceAmount: undefined,
  deductible: undefined,
  priorAuthorization: undefined,
  verificationStatus: undefined,
};

export const INITIAL_PATIENT_OTHER_CONSENT = {
  uuid: undefined,
  consentName: undefined,
  signedDate: new Date().toISOString(),
  consentDocument: undefined,
};

const INITIAL_PATIENT_ASSESSMENT_CONSENT = {
  assessment: [],
  recordedDate: undefined,
  reminderMedium: undefined,
};

export const ADD_NEW_PATIENT_INITIAL_VALUES: NewPatientForm = {
  phoneNotAvailable: false,
  emailNotAvailable: false,
  // Provider Information
  providerId: undefined,
  locationId: undefined,
  registrationDate: new Date().toISOString(),
  defaultCommunicationChannel: undefined,
  // Patient Details
  firstName: "",
  middleName: "",
  lastName: "",
  timezone: "",
  birthDate: null,
  gender: "",
  maritalStatus: undefined as unknown as PatientMaritalStatusEnum, // Enum value or undefined
  ssn: "",
  mrn: "",
  languages: undefined as unknown as any, // Enum value or undefined
  ethnicity: undefined,
  race: undefined,
  avatar: "",

  // Contact Information
  mobileNumber: undefined,
  faxNumber: "",
  homePhone: "",
  email: undefined,
  address: ADDRESS_INITIAL_VALUES,

  // Emergency Contact
  emergencyContacts: INITIAL_EM_CONTACT,

  // Insurance Information
  patientInsurances: [INITIAL_PATIENT_INSURANCE],
  // Preferences
  // preferred_pharmacies: undefined,
  // preferred_labs: undefined,
  // preferred_radiologies: undefined,

  // Privacy and Consent
  emailConsent: true,
  messageConsent: true,
  callConsent: true,
  // todo temp adding this field once done from B.E remove & name it
  pharmacyLabRadiology: null,
  pharmacyLabRadiologyMap: undefined,
  patientConsentEntities: [INITIAL_PATIENT_OTHER_CONSENT],
  isScriptSure: false,
  assessments: INITIAL_PATIENT_ASSESSMENT_CONSENT,
};

// export const EDIT_NEW_PATIENT_INITIAL_VALUES: NewPatientForm = {
//   provider_information: {
//     primary_provider: "alex_core",
//     provider_group_location: "alex_core",
//     registration_date: null,
//   },
//   patient_detail: {
//     last_name: "alex",
//     first_name: "primary",
//     timezone: "US/Alaska",
//     middle_name: "middle name",
//     dob: null,
//     gender: "male",
//     marital_status: "married",
//     ssn: "ssn-abc",
//     mrn: "1234",
//     language: "english",
//     ethnicity: "henry_bench",
//     race: "henry_bench",
//   },
//   contact_information: {
//     mobile_number: "124",
//     home_number: "123",
//     email: "asdb@gmail.com",
//     fax: "aw@ls",
//     address: {
//       address_line1: "add _1",
//       address_line2: "add_ 2",
//       city: "NYC",
//       zip_code: "22034",
//       state: "AL",
//       country: "NYC",
//     },
//   },
//   emergency_contact: {
//     relationship: "spouse",
//     last_name: "lsn",
//     first_name: "fsn",
//     phone: "9882823434",
//     email: "email@gmail.com",
//   },
//   insurance: {
//     insurance_type: "primary",
//     insurance_name: "",
//     insurance_phone_number: "",
//     insurance_fax_number: "",
//     insurance_payer: "abc",
//     member_id: "",
//     plan_id: "",
//     group_id: "",
//     group_name: "",
//     payer_contact_number: "",
//     payer_fax_number: "",
//     copay: "",
//     insurance_relationship_with_patient: "",
//   },
//   preferences: {
//     preferred_pharmacies: "prefered pharmacy",
//     preferred_labs: "labs",
//     preferred_radiologies: "radiology",
//   },
//   privacy_consent: {
//     consent_to_email: true,
//     consent_to_call: false,
//     consent_to_message: true,

//     verbal_consent: false,
//     written_consent: true,
//     digital_consent: false,
//   },
// };

// Validation schemas
const emergencyShape = {
  relationshipWithPatient: Yup.string()
    .nullable()
    .label("RelationShip With Patient")
    .test("uniqueInsuranceType", function (value, { createError, options }) {
      if (!value) return true;

      const emContacts = options?.context?.emergencyContacts;

      const dupEm = emContacts?.filter(
        (it: any) => it.relationshipWithPatient === value
      );

      if (dupEm?.length > 1) {
        return createError({
          path: this.path,
          message: `RelationShip With Patient is already selected`,
        });
      }

      return true;
    }),
  firstName: Yup.string()
    .nullable()
    .label("First Name")
    .test("alphabetsOnly", function (value, { createError }) {
      if (!value) return true;
      // Allow only alphabets
      if (!/^[a-zA-Z]+$/.test(value)) {
        return createError({
          path: this.path,
          message: "First Name should only contain alphabets",
        });
      }
      return true;
    }),
  lastName: Yup.string()
    .nullable()
    .label("Last Name")
    .test("alphabetsOnly", function (value, { createError }) {
      if (!value) return true;
      // Allow only alphabets
      if (!/^[a-zA-Z]+$/.test(value)) {
        return createError({
          path: this.path,
          message: "Last Name should only contain alphabets",
        });
      }
      return true;
    }),
  email: Yup.string()
    .nullable()
    .label("Email")
    .test("validEmail", function (value, { createError }) {
      if (!value) return true;
      // Validate email format
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return createError({
          path: this.path,
          message: "Please enter a valid email address",
        });
      }
      return true;
    }),
};

const insuranceShape = {
  backPhoto: Yup.string()
    .nullable()
    .optional()
    .trim()
    .test(
      "backPhotoFormat",
      "Only JPG, JPEG, PNG, and SVG files are allowed",
      function (value) {
        if (!value) return true;
        return !value.startsWith("otherFile");
      }
    )
    .test("backPhoto", "File size must be less than 5MB", function (value) {
      if (!value) return true;
      return !value.startsWith("size5");
    }),

  frontPhoto: Yup.string()
    .nullable()
    .optional()
    .trim()
    .test(
      "frontPhotoFormat",
      "Only JPG, JPEG, PNG, and SVG files are allowed",
      function (value) {
        if (!value) return true;
        return !value.startsWith("otherFile");
      }
    )
    .test("frontPhoto", "File size must be less than 5MB", function (value) {
      if (!value) return true;
      return !value.startsWith("size5");
    }),
  orderOfBenefits: Yup.string()
    .nullable()
    .label("Order Of Benefits")
    .test("uniqueInsuranceType", function (value, { createError, options }) {
      if (!value) return true;

      const insurances = options?.context?.patientInsurances;

      const dupInsurances = insurances?.filter(
        (it: any) => it.orderOfBenefits === value
      );

      if (dupInsurances?.length > 1) {
        return createError({
          path: this.path,
          message: `${
            replaceUnderScoreAddSpace(value?.toLocaleLowerCase()) || ""
          } insurance is already selected`,
        });
      }

      return true;
    }),
  memberId: Yup.string()
    .trim()
    .nullable()
    .optional()
    .label("Member ID")
    .matches(/^[a-zA-Z0-9]*$/, "Member ID must be alphanumeric"),

  employerAddress: Yup.object({
    zipcode: Yup.string()
      .matches(
        /^\d{5}(-\d{4})?$/,
        "Enter a valid ZIP Code (e.g., 12345 or 12345-6789)"
      )
      .nullable()
      .optional(),
  })
    .optional()
    .nullable(),
  ...SUBSCRIBER_SHAPE,
  // Effective Start Date is now restricted at selection time via the date
  // picker's minDate (set to Patient DOB) instead of Yup validation.
  // effectiveStartDate: Yup.mixed()
  //   .nullable()
  //   .test("validEffectiveStartDate", function (value) {
  //     const result = validateInsuranceEffectiveStartDate(
  //       value,
  //       this.options?.context?.birthDate
  //     );
  //     return result === true
  //       ? true
  //       : this.createError({ message: result });
  //   }),
  effectiveEndDate: Yup.mixed()
    .nullable()
    .optional()
    .test("validEffectiveEndDate", function (value) {
      if (isEmptyDate(value)) return true;
      const date = toValidDayjs(value);
      if (!date) {
        return this.createError({ message: "Please enter a valid date." });
      }
      if (date.isBefore(MIN_INSURANCE_EFFECTIVE_DATE)) {
        return this.createError({
          message: `Effective End Date cannot be earlier than ${MIN_INSURANCE_EFFECTIVE_DATE.format(
            "MM/DD/YYYY"
          )}.`,
        });
      }
      if (date.isAfter(MAX_INSURANCE_EFFECTIVE_DATE)) {
        return this.createError({
          message: `Effective End Date is unreasonably far in the future.`,
        });
      }
      const startDate = toValidDayjs(this.parent?.effectiveStartDate);
      if (startDate && !date.isAfter(startDate)) {
        return this.createError({
          message: "Effective End Date must be after Effective Start Date.",
        });
      }
      return true;
    }),
};

export const ADD_NEW_PATIENT_VALIDATION_SCHEMA = Yup.object({
  // provider_information: Yup.object({
  //   primary_provider: Yup.string(),
  //   provider_group_location: Yup.string(),
  //   registration_date: Yup.mixed(),
  // }),

  lastName: Yup.string()
    .required("Last Name is a required field.")
    .test(
      "notWhitespaceOnly",
      "Last Name cannot contain only whitespace.",
      (value) => !value || value.trim().length > 0
    )
    .matches(NAME_REGEX, "Last Name should only contain alphabetic characters.")
    .label("Last Name"),
  firstName: Yup.string()
    .required("First Name is a required field.")
    .test(
      "notWhitespaceOnly",
      "First Name cannot contain only whitespace.",
      (value) => !value || value.trim().length > 0
    )
    .matches(
      NAME_REGEX,
      "First Name should only contain alphabetic characters."
    )
    .label("First Name"),
  birthDate: Yup.string()
    .nullable()
    .required("Date Of Birth is a required field.")
    .test(
      "not-future",
      "Date of Birth cannot be in the future.",
      (value) => !value || !dayjs(value).isAfter(dayjs(), "day")
    )
    .test("valid-dob", "Please enter a valid Date of Birth.", (value) => {
      if (!value) return true;
      const d = dayjs(value);
      return d.isValid() && d.year() >= 1900;
    })
    .label("Date Of Birth"),
  timezone: Yup.string()
    .nullable()
    .required("Timezone is a required field.")
    .label("Timezone"),

  phoneNotAvailable: Yup.boolean().optional().nullable(),
  emailNotAvailable: Yup.boolean().optional().nullable(),
  email: Yup.string()
    .email()
    .matches(/.*@.+\..+/, "Email address must be a valid Email.")
    .when("emailNotAvailable", {
      is: (isAvail: boolean) => !!isAvail,
      then: (schema) => schema?.optional().nullable(),
      otherwise: (schema) =>
        schema?.required("Email is a required field.")?.label("Email"),
    }),
  mobileNumber: Yup.string()
    //   .test("removedBracket", function (value, { createError }) {
    //     if (value === "(") {
    //       return createError({
    //         path: this.path,
    //         message: "Mobile Number is a required Field",
    //       });
    //     }

    //     return true;
    //   })
    // .required()
    // .label("Mobile Number")
    .min(12, "Mobile Number must be at least 8 characters.")
    .when("phoneNotAvailable", {
      is: (isAvail: boolean) => !!isAvail,
      then: (schema) => schema?.optional().nullable(),
      otherwise: (schema) =>
        schema
          ?.required("Mobile Number is a required field.")
          ?.label("Mobile Number"),
    }),

  gender: Yup.string().required("Gender is a required field.").label("Gender"),
  patientInsurances: Yup.array().of(Yup.object(insuranceShape)),

  emergencyContacts: Yup.array().of(Yup.object(emergencyShape)),
  // marital_status: Yup.string().required().label("Marital Status"),
  ssn: Yup.string()
    .min(11, "SSN number must be of 9 digits")
    .optional()
    .nullable(),
  // language: Yup.string(),
  // ethnicity: Yup.string(),
  // race: Yup.string(),

  isScriptSure: Yup.boolean().default(false).nullable(),
  address: Yup.object().shape({
    line1: Yup.string().when("$isScriptSure", {
      is: true,
      then: (schema) => schema.required("Address Line 1 is a required field."),
      otherwise: (schema) => schema.optional().nullable(),
    }),
    city: Yup.string().when("$isScriptSure", {
      is: true,
      then: (schema) => schema.required("City is a required field."),
      otherwise: (schema) => schema.optional().nullable(),
    }),
    state: Yup.string().when("$isScriptSure", {
      is: true,
      then: (schema) => schema.required("State is a required field."),
      otherwise: (schema) => schema.optional().nullable(),
    }),
    country: Yup.string().when("$isScriptSure", {
      is: true,
      then: (schema) => schema.required("Country is a required field."),
      otherwise: (schema) => schema.optional().nullable(),
    }),
    zipcode: Yup.string()
      .matches(
        /^\d{5}(-\d{4})?$/,
        "Enter a valid ZIP Code (e.g., 12345 or 12345-6789)"
      )
      .when("$isScriptSure", {
        is: true,
        then: (schema) => schema.required("ZIP Code is a required field."),
        otherwise: (schema) => schema.optional().nullable(),
      }),
  }),

  assessments: Yup.object({
    reminderMedium: Yup.string().when("assessment.length", {
      is: (length: number) => length > 0,
      then: (schema) => schema.required().label("Reminder Medium"),
      otherwise: (schema) => schema.notRequired(),
    }),
  }).nullable(),

  // emergency_contact: Yup.object({
  //   relationship: Yup.string().required().label("Relationship"),
  //   last_name: Yup.string().required().label("Last Name"),
  //   first_name: Yup.string().required().label("First Name"),
  //   phone: Yup.string().required().label("Phone"),
  //   email: Yup.string().required().label("Email"),
  // }),

  // insurance: Yup.object({
  //   insurance_type: Yup.string(),
  //   insurance_payer: Yup.string(),
  //   member_id: Yup.string(),
  //   plan_id: Yup.string(),
  //   group_id: Yup.string(),
  //   group_name: Yup.string(),
  //   payer_contact_number: Yup.string(),
  //   payer_fax_number: Yup.string(),
  //   copay: Yup.string(),
  //   insurance_relationship_with_patient: Yup.string(),
  // }),

  // preferences: Yup.object({
  //   preferred_pharmacies: Yup.string().required().label("Pharmacies"),
  //   preferred_labs: Yup.string().required().label("lab"),
  //   preferred_radiologies: Yup.string().required().label("radiology"),
  // }),

  // privacy_consent: Yup.object({
  //   consent_to_email: Yup.boolean(),
  //   consent_to_call: Yup.boolean(),
  //   consent_to_message: Yup.boolean(),
  // }),
});

export const PATIENT_FORM_VALIDATION_SCHEMA = Yup.object({
  lastName: Yup.string()
    .required("Last Name is a required field.")
    .label("Last Name"),
  firstName: Yup.string()
    .required("First Name is a required field.")
    .label("First Name"),
  birthDate: Yup.string()
    .nullable()
    .required("Date Of Birth is a required field.")
    .test(
      "not-future",
      "Date of Birth cannot be in the future.",
      (value) => !value || !dayjs(value).isAfter(dayjs(), "day")
    )
    .test("valid-dob", "Please enter a valid Date of Birth.", (value) => {
      if (!value) return true;
      const d = dayjs(value);
      return d.isValid() && d.year() >= 1900;
    })
    .label("Date Of Birth"),
  timezone: Yup.string()
    .nullable()
    .required("Timezone is a required field.")
    .label("Timezone"),

  phoneNotAvailable: Yup.boolean().optional().nullable(),
  emailNotAvailable: Yup.boolean().optional().nullable(),
  email: Yup.string()
    .email()
    .matches(/.*@.+\..+/, "Email address must be a valid Email.")
    .when("emailNotAvailable", {
      is: (isAvail: boolean) => !!isAvail,
      then: (schema) => schema?.optional().nullable(),
      otherwise: (schema) =>
        schema?.required("Email is a required field.")?.label("Email"),
    }),
  mobileNumber: Yup.string()
    //   .test("removedBracket", function (value, { createError }) {
    //     if (value === "(") {
    //       return createError({
    //         path: this.path,
    //         message: "Mobile Number is a required Field",
    //       });
    //     }

    //     return true;
    //   })
    // .required()
    // .label("Mobile Number")
    .min(12, "Mobile Number must be at least 8 characters.")
    .when("phoneNotAvailable", {
      is: (isAvail: boolean) => !!isAvail,
      then: (schema) => schema?.optional().nullable(),
      otherwise: (schema) =>
        schema
          ?.required("Mobile Number is a required field.")
          ?.label("Mobile Number"),
    }),

  gender: Yup.string().required("Gender is a required field.").label("Gender"),

  emergencyContacts: Yup.array().of(Yup.object(emergencyShape)),
});
